import { Box, Divider, Grid, Typography } from "@mui/material";
import MediaTypeAnalyticsChart from "./MediaTypeAnalyticsChart";
import AnalyticWidget from "./AnalyticWidget";
import InteractionAnalyticsBarChart from "./InteractionAnalyticsBarChart";
import { useState, useEffect } from "react";
import { useDashboardData } from "../../hooks/useDashboardData";
import { useParams } from "react-router-dom";
import {
  InteractionByMediaType,
  InteractionsByYear,
  InteractionsByTags,
} from "../../types/dashboard/Dashboard";
import dayjs from "dayjs";
import { DeleteSweep, Pause, CheckCircle } from "@mui/icons-material";

import TagWidget from "./TagWidget";
import { useTranslation } from "react-i18next";
import { numberWithCommas } from "../../helpers/numberWithCommas";

export const formatTime = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
};

const Dashboard = () => {
  const { t } = useTranslation("dashboards");
  const [countLegalHold, setCountLegalHold] = useState<number | undefined>();
  const [countInteractionsExpiring, setCountInteractionsExpiring] = useState<
    number | undefined
  >();
  const [interactionsByYear, setInteractionsByYear] = useState<
    InteractionsByYear | undefined
  >();
  const { customerId } = useParams();
  const { data, isLoading } = useDashboardData(customerId);
  const [mediaTypeInteraction, setMediaTypeInteraction] = useState<
    InteractionByMediaType[] | null
  >([]);
  const [totalInteractions, setTotalInteractions] = useState<number | null>(
    null
  );
  const [totalInteractionsWithoutMedia, setTotalInteractionsWithoutMedia] =
    useState<number | null>(null);

  const [tagsData, setTagsData] = useState<InteractionsByTags[] | null>([]);
  // set up links for each analytic widget
  const legalHoldLink = `/customers/${customerId}/search?legalHold=true`;
  const searchLink = `/customers/${customerId}/search`;

  const today = dayjs().utc();
  const thirtyDaysOut = today.add(30, "day").utc();

  const dateParams = new URLSearchParams({
    fromRetention: today.format("YYYY-MM-DDTHH:mm:ss"),
    toRetention: thirtyDaysOut.format("YYYY-MM-DDTHH:mm:ss"),
    hasRetention: "true",
  });

  const purgingLink = `/customers/${customerId}/search?${dateParams.toString()}`;

  useEffect(() => {
    if (isLoading || !data) return;

    setCountLegalHold(data.countLegalHold);
    setCountInteractionsExpiring(data.countInteractionsExpiring);
    setMediaTypeInteraction(data.interactionByType);
    setInteractionsByYear(data.interactionsByYear);
    setTagsData(data.interactionsByTags);
    setTotalInteractions(data.interactionTotal);
    setTotalInteractionsWithoutMedia(data.interactionTotalWithoutMedia);
  }, [isLoading, data]);

  return (
    <Box
      sx={{
        padding: 4,
      }}
    >
      <Grid container spacing={3} sx={{ alignItems: "stretch" }}>
        {/* Top row - 3 widgets */}
        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: "flex" }}>
          <AnalyticWidget
            title={t("dashboards:interactionsInLegalHold")}
            icon={
              <Pause
                fontSize="large"
                sx={{
                  color: "white",
                }}
              />
            }
            isLoading={isLoading}
            tooltipContent={t("dashboards:interactionsInLegalHoldTooltip")}
            linkTo={legalHoldLink}
          >
            <SimpleMetricText total={countLegalHold} />
          </AnalyticWidget>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: "flex" }}>
          <AnalyticWidget
            title={t("dashboards:purging30Days")}
            icon={
              <DeleteSweep
                fontSize="large"
                sx={{
                  color: "white",
                }}
              />
            }
            isLoading={isLoading}
            tooltipContent={t("dashboards:purging30DaysTooltip")}
            linkTo={purgingLink}
          >
            <SimpleMetricText total={countInteractionsExpiring} />
          </AnalyticWidget>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: "flex" }}>
          <AnalyticWidget
            title="Total Interactions"
            isLoading={isLoading}
            linkTo={searchLink}
            icon={
              <CheckCircle
                fontSize="large"
                sx={{
                  color: "white",
                }}
              />
            }
          >
            <Box display="flex" gap={2} alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="h5" color="secondary">
                  {totalInteractions?.toLocaleString()}
                </Typography>
              </Box>
              <Divider orientation="vertical" variant="middle" flexItem />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  No Media
                </Typography>
                <Typography variant="h5" color="secondary">
                  {totalInteractionsWithoutMedia?.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </AnalyticWidget>
        </Grid>
        <Grid item xs={12} sm={6} md={12} lg={3} sx={{ display: "flex" }}>
          <TagWidget isLoading={isLoading} tagData={tagsData ?? []} />
        </Grid>

        {/* Second row - Charts */}
        <Grid item xs={12} md={12} lg={8}>
          <InteractionAnalyticsBarChart
            interactionsByYear={interactionsByYear}
            isBarChartLoading={isLoading}
          />
        </Grid>
        <Grid item xs={12} md={12} lg={4}>
          <MediaTypeAnalyticsChart
            data={mediaTypeInteraction}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;

function SimpleMetricText({ total, units }: { total?: number; units?: string }) {
  return (
    <Typography variant="h4" color="secondary">
      {numberWithCommas(total)} {units}
    </Typography>
  );
}
